<template>
  <v-card dark flat color="#1e1e1e" class="competition-manager">
    <MarketingToolHeader eyebrow="Marketing" title="Wettbewerbe" description="Wettbewerbe verwalten, Links teilen und Teilnahmen auswerten." icon="mdi-trophy-outline">
      <template v-slot:actions><v-btn color="#01963f" dark depressed @click="openCreate" :disabled="busy || !configs.length">Neuer Wettbewerb</v-btn></template>
    </MarketingToolHeader>
    <v-alert v-if="error" type="error" outlined class="mt-5" role="alert">{{ error }}</v-alert>
    <v-alert v-if="notice" type="success" outlined class="mt-5" role="status">{{ notice }}</v-alert>
    <v-progress-linear v-if="loading" indeterminate color="#01963f" class="mt-5" />
    <v-btn v-if="!loading && !configs.length" text @click="load">Erneut laden</v-btn>

    <section v-if="view === 'overview' && configs.length" aria-labelledby="overview-title" class="competition-overview">
      <div class="overview-heading"><div><h2 id="overview-title" ref="overviewTitle" tabindex="-1">Deine Wettbewerbe</h2><p>{{ configs.length }} {{ configs.length === 1 ? 'Wettbewerb' : 'Wettbewerbe' }} · Wähle, was du bearbeiten möchtest.</p></div></div>
      <section v-for="group in competitionGroups" :key="group.id" class="competition-group" :aria-labelledby="`group-${group.id}`">
        <div class="group-heading"><h3 :id="`group-${group.id}`">{{ group.title }} <span class="group-count">{{ group.items.length }}</span></h3><p>{{ group.description }}</p></div>
        <p v-if="!group.items.length" class="group-empty">{{ group.id === 'active' ? 'Aktuell ist kein Wettbewerb aktiv.' : 'Keine pausierten Wettbewerbe vorhanden.' }}</p>
        <div v-else class="competition-grid">
        <article v-for="item in group.items" :key="item.slug" class="competition-card">
          <div class="card-topline"><span class="card-symbol" aria-hidden="true"><v-icon>mdi-trophy-outline</v-icon></span><span class="status-pill" :class="{ 'status-open': statusFor(item) === 'Teilnahme offen' }">{{ statusFor(item) }}</span></div>
          <h4>{{ item.title }}</h4>
          <p class="card-prize">{{ item.prize || 'Gewinn noch offen' }}</p>
          <dl class="card-facts"><div><dt>Teilnahmeschluss</dt><dd>{{ formatDay(item.closesOn) }}</dd></div><div><dt>Fragen</dt><dd>{{ item.questions.length }}</dd></div></dl>
          <div class="card-primary-actions">
            <v-btn color="#01963f" dark depressed :disabled="busy" @click="openCompetition(item.slug, 0)"><v-icon left small>mdi-tune</v-icon>Konfiguration</v-btn>
            <v-btn outlined :disabled="busy || !item.revision" @click="openCompetition(item.slug, 1)"><v-icon left small>mdi-account-group-outline</v-icon>Teilnehmer <span class="participant-count">{{ countLabel(item) }}</span></v-btn>
          </div>
          <p v-if="!item.revision" class="card-draft-note">Entwurf · Zum Verwalten von Teilnahmen zuerst speichern.</p>
          <div class="card-share-actions">
            <v-btn text :disabled="!canShare(item)" @click="copyLink(item)"><v-icon left small>mdi-content-copy</v-icon>Link kopieren</v-btn>
            <v-btn text :disabled="busy || !item.revision" @click="download('qr', item.slug)"><v-icon left small>mdi-qrcode</v-icon>QR-Code</v-btn>
            <v-btn icon :href="linkFor(item)" target="_blank" rel="noopener" :disabled="!canShare(item)" :aria-label="`${item.title}: Webseite öffnen`"><v-icon small>mdi-open-in-new</v-icon></v-btn>
          </div>
        </article>
      </div>
      </section>
    </section>

    <template v-if="view === 'detail' && config">
      <div class="detail-heading">
        <v-btn text :disabled="busy" @click="showOverview"><v-icon left small>mdi-arrow-left</v-icon>Alle Wettbewerbe</v-btn>
        <div class="detail-title"><h2 ref="detailTitle" tabindex="-1">{{ config.title }}</h2><span class="status-pill">{{ statusLabel }}</span></div>
      </div>
      <section class="manager-toolbar" aria-label="Wettbewerb teilen">
        <v-btn outlined :disabled="!canShare(selectedConfig)" @click="copyLink(selectedConfig)"><v-icon left small>mdi-content-copy</v-icon>Link kopieren</v-btn>
        <v-btn outlined :disabled="busy || !saved" @click="download('qr')"><v-icon left small>mdi-qrcode</v-icon>QR-Code</v-btn>
        <v-btn text :href="publicLink" target="_blank" rel="noopener" :disabled="!canShare(selectedConfig)">Seite ansehen <v-icon right small>mdi-open-in-new</v-icon></v-btn>
      </section>
      <p class="link-note">{{ publicLink }} · Geteilt wird der zuletzt gespeicherte Link.</p>
      <v-alert v-if="config.example && tab === 0" type="warning" outlined>Beispielentwurf: Datum und Teilnahmebedingungen vor der Freischaltung ersetzen. In der Vorschau werden keine Teilnahmen gespeichert.</v-alert>

      <v-tabs :value="tab" color="#58d388" background-color="transparent" @change="changeTab"><v-tab>Konfiguration</v-tab><v-tab :disabled="!saved">Teilnehmer <span class="participant-count">{{ countLabel(selectedConfig) }}</span></v-tab></v-tabs>
      <section v-if="tab === 0" class="editor-grid">
        <div class="editor-card">
          <h2>Der Wettbewerb</h2><p class="section-note">Diese Angaben erscheinen auf der Webseite.</p>
          <v-text-field v-model="config.title" label="Titel" outlined maxlength="100" :disabled="busy" />
          <v-text-field v-model.trim="config.publicSlug" label="Link-Kürzel" prefix="/wettbewerb/" outlined maxlength="60" :disabled="busy" hint="Kleinbuchstaben, Zahlen und Bindestriche. Nach dem Speichern nutzen neue QR-Codes diesen Link; bisherige Links bleiben gültig." persistent-hint />
          <v-text-field v-model="config.prize" label="Gewinn" outlined maxlength="120" :disabled="busy" />
          <v-textarea v-model="config.description" label="Einleitung" outlined rows="3" auto-grow maxlength="600" :disabled="busy" />
          <v-text-field v-model="config.closesOn" label="Teilnahmeschluss (inklusive, Schweizer Zeit)" type="date" outlined :disabled="busy" />
          <v-textarea v-model="config.terms" label="Teilnahmebedingungen" outlined rows="8" auto-grow maxlength="20000" :disabled="busy" />
          <p class="section-note">Die Werbeeinwilligung ist freiwillig und wird separat mit jeder Teilnahme gespeichert. Eine Teilnahme löst keine automatische Werbenachricht aus.</p>
        </div>
        <div class="questions-editor">
          <div v-if="!config.questions.length" class="editor-card"><h2>Noch keine Fragen</h2><p>Füge deine erste Frage hinzu und trage die Antwortmöglichkeiten ein.</p></div>
          <section v-for="(question, index) in config.questions" :key="question.id" class="editor-card">
            <div class="question-heading"><h2>Frage {{ index + 1 }}</h2><v-btn icon :disabled="busy" :aria-label="`Frage ${index + 1} entfernen`" @click="removeQuestion(index)"><v-icon>mdi-delete-outline</v-icon></v-btn></div>
            <v-textarea v-model="question.text" label="Frage" outlined rows="2" auto-grow maxlength="300" :disabled="busy" />
            <p class="section-note">Markiere die richtige Antwort für die automatische Bewertung.</p>
            <v-radio-group v-model="question.correct" :disabled="busy">
              <div v-for="(option, choice) in question.options" :key="choice" class="option-row">
                <v-radio :value="choice" :aria-label="`Antwort ${choice + 1} ist richtig`" color="#01963f" />
                <v-text-field v-model="question.options[choice]" :label="`Antwort ${choice + 1}`" outlined hide-details maxlength="160" :disabled="busy" />
                <v-btn icon :disabled="busy || question.options.length <= 2" :aria-label="`Antwort ${choice + 1} entfernen`" @click="removeOption(question, choice)"><v-icon>mdi-close</v-icon></v-btn>
              </div>
            </v-radio-group>
            <v-btn text :disabled="busy || question.options.length >= 6" @click="question.options.push('')"><v-icon left small>mdi-plus</v-icon>Antwort hinzufügen</v-btn>
          </section>
          <v-btn outlined :disabled="busy || config.questions.length >= 50" @click="addQuestion"><v-icon left small>mdi-plus</v-icon>Frage hinzufügen</v-btn>
        </div>
        <div class="save-bar">
          <p>Bereits eingegangene Teilnahmen behalten ihre ursprünglichen Fragen, Bedingungen und Bewertung.</p>
          <div class="save-actions">
            <v-btn outlined :disabled="busy || !dirty" @click="reset">Änderungen verwerfen</v-btn>
            <v-btn outlined :loading="saving" :disabled="busy" @click="save(config.active)">Änderungen speichern</v-btn>
            <v-btn v-if="!config.active" color="#01963f" dark depressed :disabled="busy" @click="publishDialog = true">Freischalten</v-btn>
            <v-btn v-else color="warning" outlined :disabled="busy" @click="save(false)">Teilnahme pausieren</v-btn>
          </div>
        </div>
      </section>
      <section v-else class="editor-card entries-panel">
        <div class="entries-heading"><div><h2>Teilnahmen &amp; Bewertung</h2><p>{{ total }} Teilnahmen · Bewertung anhand der jeweils eingereichten Version</p></div>
          <div class="save-actions"><v-btn text :disabled="busy" @click="fetchEntries">Aktualisieren</v-btn><v-btn color="#01963f" dark depressed :loading="downloading" :disabled="busy || !total" @click="download('export')"><v-icon left small>mdi-file-excel-outline</v-icon>Excel herunterladen</v-btn></div>
        </div>
        <v-simple-table><thead><tr><th>Datum</th><th>Name</th><th>Kontakt</th><th>Richtig</th><th>Werbeeinwilligung</th></tr></thead>
          <tbody><tr v-for="entry in entries" :key="entry._id"><td>{{ formatDate(entry.createdAt) }}</td><td>{{ entry.firstName }} {{ entry.lastName }}</td><td>{{ entry.email }}<br />{{ entry.phone }}</td><td><strong>{{ entry.score }} / {{ entry.total }}</strong></td><td>{{ entry.marketing ? 'Ja' : 'Nein' }}</td></tr>
            <tr v-if="!entries.length"><td colspan="5">{{ entriesLoading ? 'Teilnahmen werden geladen …' : 'Noch keine Teilnahmen vorhanden.' }}</td></tr>
          </tbody>
        </v-simple-table>
        <div class="pagination"><v-btn text :disabled="busy || page === 0" @click="changePage(-1)">Zurück</v-btn><span>Seite {{ page + 1 }} von {{ Math.max(1, Math.ceil(total / 50)) }}</span><v-btn text :disabled="busy || (page + 1) * 50 >= total" @click="changePage(1)">Weiter</v-btn></div>
      </section>
    </template>

    <v-snackbar v-model="copied" color="#087638" :timeout="2500" role="status">Link kopiert.</v-snackbar>
    <v-dialog v-model="copyDialog" max-width="580"><v-card dark color="#262a28" class="competition-dialog"><v-card-title>Link kopieren</v-card-title><v-card-text>
      <p>Automatisches Kopieren ist hier nicht verfügbar. Markiere den Link und kopiere ihn manuell.</p>
      <v-text-field :value="copyValue" readonly outlined label="Wettbewerbslink" @focus="$event.target.select()" />
    </v-card-text><v-card-actions><v-spacer /><v-btn text @click="copyDialog = false">Schliessen</v-btn></v-card-actions></v-card></v-dialog>
    <v-dialog v-model="publishDialog" max-width="520"><v-card dark color="#262a28" class="competition-dialog"><v-card-title>Wettbewerb freischalten?</v-card-title><v-card-text>Damit wird der aktuelle Inhalt gespeichert und die Teilnahme auf der Webseite geöffnet. Bitte prüfe Gewinn, Lösungen, Teilnahmeschluss und Teilnahmebedingungen.</v-card-text><v-card-actions><v-btn text @click="publishDialog = false">Zurück</v-btn><v-spacer /><v-btn color="#01963f" dark depressed @click="publishDialog = false; save(true)">Jetzt freischalten</v-btn></v-card-actions></v-card></v-dialog>
    <v-dialog v-model="createDialog" max-width="520"><v-card dark color="#262a28" class="competition-dialog"><v-card-title>Neuer Wettbewerb</v-card-title><v-card-text>
      <p>Erstellt einen pausierten Entwurf mit leerer Fragenliste. Die übrigen Angaben werden als Vorlage übernommen; bisherige Teilnahmen bleiben beim ursprünglichen Wettbewerb.</p>
      <v-text-field v-model="newTitle" label="Titel" maxlength="100" outlined />
      <v-text-field v-model="newSlug" label="Link-Kürzel, z. B. fruehling-2027" maxlength="60" outlined hint="Kleinbuchstaben, Zahlen und Bindestriche. Der öffentliche Link lässt sich später ändern." persistent-hint />
      <p v-if="createError" role="alert" class="error--text">{{ createError }}</p>
    </v-card-text><v-card-actions><v-btn text @click="createDialog = false">Abbrechen</v-btn><v-spacer /><v-btn color="#01963f" dark depressed @click="createDraft">Entwurf anlegen</v-btn></v-card-actions></v-card></v-dialog>
  </v-card>
</template>

<script>
import api from "@/api/competitionApi"
import MarketingToolHeader from "./MarketingToolHeader.vue"

const clone = (value) => JSON.parse(JSON.stringify(value))
export default {
  name: "CommunicationCompetition",
  components: { MarketingToolHeader },
  data: () => ({ view: "overview", copied: false, copyDialog: false, copyValue: "", configs: [], config: null, selected: "", baseline: "", loading: false, saving: false, downloading: false, entriesLoading: false,
    error: "", notice: "", tab: 0, entries: [], total: 0, page: 0, publishDialog: false, createDialog: false, newTitle: "", newSlug: "", createError: "" }),
  computed: {
    busy() { return this.loading || this.saving || this.downloading || this.entriesLoading },
    dirty() { return this.config && JSON.stringify(this.config) !== this.baseline },
    saved() { return this.config && this.config.revision > 0 },
    selectedConfig() { return this.configs.find((item) => item.slug === this.selected) || null },
    publicLink() { return this.selectedConfig ? this.linkFor(this.selectedConfig) : "" },
    statusLabel() { return this.statusFor(this.selectedConfig) },
    competitionGroups() {
      const active = [], paused = []
      this.configs.forEach((item) => { (this.statusFor(item) === "Teilnahme offen" ? active : paused).push(item) })
      return [
        { id: "active", title: "Aktiv", description: "Zur Teilnahme freigeschaltet.", items: active },
        { id: "paused", title: "Pausiert", description: "Entwürfe, pausierte und beendete Wettbewerbe.", items: paused },
      ]
    },
  },
  created() { this.load() },
  beforeRouteLeave(to, from, next) { next(!this.dirty || window.confirm("Ungespeicherte Änderungen verwerfen?")) },
  methods: {
    message(error) { return error.response && error.response.data && error.response.data.error || "Das hat leider nicht geklappt. Bitte versuche es erneut." },
    async load() {
      this.loading = true; this.error = ""
      try { this.configs = await api.list(); this.view = "overview"; this.config = null; this.selected = ""; this.baseline = "" }
      catch (error) { this.error = this.message(error) }
      finally { this.loading = false; this.$emit("finishedloading") }
    },
    select(slug, force = false) {
      if (!force && this.dirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return
      const config = this.configs.find((item) => item.slug === slug)
      if (!config) return
      this.selected = slug; this.config = { ...clone(config), publicSlug: config.publicSlug || config.slug }; this.baseline = JSON.stringify(this.config)
      this.entries = []; this.total = 0; this.page = 0; this.tab = 0; this.notice = ""; this.error = ""
      this.view = "detail"
      return true
    },
    openCompetition(slug, tab = 0) {
      if (!this.select(slug)) return
      this.changeTab(tab)
      this.focusHeading("detailTitle")
    },
    changeTab(tab) {
      if (!this.config || (tab === 1 && !this.saved) || this.entriesLoading) return
      this.tab = tab
      if (tab === 1) this.fetchEntries()
    },
    showOverview() {
      if (this.dirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return
      if (this.config && !this.baseline) this.configs = this.configs.filter((item) => item.slug !== this.selected)
      this.view = "overview"; this.config = null; this.selected = ""; this.baseline = ""; this.error = ""; this.notice = ""
      this.focusHeading("overviewTitle")
    },
    focusHeading(name) {
      this.$nextTick(() => {
        const heading = this.$refs[name]
        if (heading) { heading.focus({ preventScroll: true }); heading.scrollIntoView({ block: "nearest", behavior: "auto" }) }
      })
    },
    linkFor(item) { return `https://wellcomefit.ch/wettbewerb/${item.publicSlug || item.slug}` },
    countLabel(item) {
      if (item && Number.isInteger(item.participantCount)) return item.participantCount.toLocaleString("de-CH")
      return item && item.revision > 0 ? "–" : "0"
    },
    canShare(item) { return Boolean(item && (item.revision > 0 || item.slug === "olma-2026")) },
    statusFor(item) {
      if (!item || !item.active || item.example) return "Entwurf / pausiert"
      const today = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date())
      return item.closesOn < today ? "Beendet" : "Teilnahme offen"
    },
    formatDay(value) { return value ? new Date(`${value}T12:00:00Z`).toLocaleDateString("de-CH") : "Noch offen" },
    async copyLink(item) {
      if (!this.canShare(item)) return
      this.copied = false; this.copyDialog = false
      this.copyValue = this.linkFor(item)
      try {
        await navigator.clipboard.writeText(this.copyValue)
        this.copied = true
      } catch (_) { this.copyDialog = true }
    },
    reset() {
      if (!this.baseline) {
        this.configs = this.configs.filter((item) => item.slug !== this.selected)
        this.select(this.configs[0].slug, true)
      } else { this.config = JSON.parse(this.baseline); this.notice = "" }
    },
    addQuestion() {
      if (this.busy || this.config.questions.length >= 50) return
      this.config.questions.push({ id: `question-${window.crypto.randomUUID()}`, text: "", options: ["", ""], correct: null })
    },
    removeQuestion(index) {
      const question = this.config.questions[index]
      if ((question.text || question.options.some(Boolean)) && !window.confirm("Diese Frage aus dem Wettbewerb entfernen? Bereits gespeicherte Teilnahmen bleiben erhalten.")) return
      this.config.questions.splice(index, 1)
    },
    removeOption(question, index) {
      question.options.splice(index, 1)
      if (question.correct === index) question.correct = null
      else if (question.correct > index) question.correct -= 1
    },
    async save(active) {
      if (this.busy) return
      this.saving = true; this.error = ""; this.notice = ""
      try {
        const saved = { ...await api.save({ ...clone(this.config), active }), participantCount: this.selectedConfig.participantCount }
        const index = this.configs.findIndex((item) => item.slug === saved.slug)
        this.$set(this.configs, index, saved); this.config = clone(saved); this.baseline = JSON.stringify(this.config)
        this.notice = active ? "Gespeichert. Der Wettbewerb ist freigeschaltet." : "Entwurf gespeichert. Die Teilnahme ist pausiert."
      } catch (error) { this.error = this.message(error) }
      finally { this.saving = false }
    },
    async fetchEntries() {
      if (!this.saved) return
      this.entriesLoading = true; this.error = ""
      try {
        const result = await api.entries(this.selected, this.page)
        this.entries = result.entries; this.total = result.total
        this.$set(this.selectedConfig, "participantCount", result.total)
      }
      catch (error) { this.error = this.message(error); this.entries = [] }
      finally { this.entriesLoading = false }
    },
    changePage(amount) { this.page += amount; this.fetchEntries() },
    async download(type, slug = this.selected) {
      this.downloading = true; this.error = ""
      try { await api.download(slug, type) } catch (error) { this.error = this.message(error) }
      finally { this.downloading = false }
    },
    formatDate(date) { return new Date(date).toLocaleString("de-CH", { timeZone: "Europe/Zurich" }) },
    openCreate() { this.newTitle = ""; this.newSlug = ""; this.createError = ""; this.createDialog = true },
    createDraft() {
      if (!this.newTitle.trim() || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(this.newSlug) || this.newSlug.length > 60) { this.createError = "Bitte Titel und ein gültiges Link-Kürzel eingeben."; return }
      if (this.configs.some((item) => item.slug === this.newSlug)) { this.createError = "Dieses Link-Kürzel ist bereits vergeben."; return }
      if (this.dirty && !window.confirm("Die übrigen ungespeicherten Angaben als Vorlage übernehmen? Die neue Fragenliste bleibt leer.")) return
      const draft = { ...clone(this.config || this.configs[0]), slug: this.newSlug, publicSlug: this.newSlug, title: this.newTitle.trim(), revision: 0, active: false, example: true, questions: [], participantCount: 0 }
      delete draft._id
      delete draft.links
      this.configs.unshift(draft); this.createDialog = false; this.select(draft.slug, true)
      // A newly created local draft must be saved before navigation or QR download.
      this.baseline = ""
      this.focusHeading("detailTitle")
    },
  },
}
</script>

<style scoped>
.competition-manager { padding: 1.5rem; max-width: 100rem; margin: auto; font-size: 1rem; color: #f4f7f5; }
.manager-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 1rem; margin: 1.5rem 0 .75rem; }
.competition-overview { margin-top: 2rem; }
.overview-heading { margin-bottom: 1.5rem; }
.overview-heading h2, .detail-title h2 { font-size: 1.5rem; line-height: 1.3; }
.overview-heading p { font-size: .875rem; color: #c6cec9; margin: .5rem 0 0; }
.competition-group { margin-top: 2rem; }
.group-heading { margin-bottom: 1rem; }
.group-heading h3 { display: flex; align-items: center; gap: .625rem; font-size: 1.25rem; }
.group-heading p { color: #aebdb3; font-size: .875rem; margin: .375rem 0 0; }
.group-count, .participant-count { display: inline-flex; align-items: center; justify-content: center; min-width: 1.5rem; padding: .125rem .5rem; border-radius: 1rem; background: #ffffff1a; color: #e4eee7; font-size: .75rem; font-weight: 600; line-height: 1.5; }
.participant-count { margin-left: .5rem; }
.group-empty { padding: 1.25rem; border: 1px dashed #ffffff26; border-radius: 1rem; color: #aebdb3; font-size: .875rem; }
.competition-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 21rem), 1fr)); gap: 1.25rem; }
.competition-card { display: flex; flex-direction: column; min-width: 0; padding: 1.5rem; background: linear-gradient(145deg, #28332c, #242825 55%); border: 1px solid #ffffff26; border-radius: 1.25rem; }
.card-topline { display: flex; align-items: center; justify-content: space-between; gap: .75rem; margin-bottom: 1.5rem; }
.card-symbol { display: grid; place-items: center; width: 2.75rem; height: 2.75rem; border-radius: .875rem; background: #01963f24; }
.card-symbol .v-icon { color: #58d388; }
.competition-card h4 { font-size: 1.375rem; line-height: 1.3; margin: 0 0 .5rem; overflow-wrap: anywhere; }
.card-prize { font-size: 1rem; color: #d3ddd6; margin-bottom: 1.5rem; overflow-wrap: anywhere; }
.card-facts { display: flex; flex-wrap: wrap; gap: 1rem 2rem; margin-bottom: 1.5rem; }
.card-facts dt { font-size: .75rem; color: #aebdb3; margin-bottom: .25rem; }
.card-facts dd { font-size: 1rem; font-weight: 600; margin: 0; }
.card-primary-actions { display: flex; flex-wrap: wrap; gap: .75rem; margin-top: auto; }
.card-primary-actions .v-btn { flex: 1 1 10rem; min-height: 2.75rem; padding: 0 .75rem; }
.card-share-actions { display: flex; align-items: center; flex-wrap: wrap; gap: .25rem; border-top: 1px solid #ffffff1f; margin-top: 1.25rem; padding-top: .75rem; }
.card-share-actions .v-btn { min-height: 2.75rem; }
.card-draft-note { font-size: .875rem; color: #aebdb3; margin: .875rem 0 0; }
.status-pill.status-open { color: #8ae0a8; background: #01963f24; }
.detail-heading { margin-top: 1.5rem; }
.detail-title { display: flex; flex-wrap: wrap; align-items: center; gap: 1rem; margin-top: 1rem; }
.detail-title h2 { overflow-wrap: anywhere; }
.detail-title h2:focus, .overview-heading h2:focus { outline: none; }
.status-pill { padding: .5rem .875rem; border-radius: 2rem; background: #01963f20; color: inherit; font-size: .875rem; }
.link-note { font-size: .875rem; overflow-wrap: anywhere; opacity: .8; }
.editor-grid { display: grid; grid-template-columns: 1fr 1fr; align-items: start; gap: 1.5rem; margin-top: 1.5rem; }
.editor-card { border: 1px solid #ffffff26; border-radius: 1.125rem; padding: 1.5rem; background: #262a28; }
.editor-card h2 { font-size: 1.25rem; margin-bottom: 1rem; }
.editor-card p { font-size: .875rem; line-height: 1.65; }
.section-note { color: #c6cec9; }
.question-heading { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.question-heading h2 { margin-bottom: 0; }
.questions-editor { display: grid; gap: 1rem; min-width: 0; }
.option-row { display: flex; align-items: center; gap: .75rem; margin-bottom: .75rem; }
.option-row > .v-radio { margin: 0; }
.save-bar { grid-column: 1 / -1; border: 1px solid #8884; border-radius: 1rem; padding: 1.5rem; }
.save-bar p { font-size: .875rem; }
.save-actions { display: flex; flex-wrap: wrap; gap: .75rem; }
.entries-panel { margin-top: 1.5rem; }
.entries-heading { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.pagination { display: flex; align-items: center; justify-content: flex-end; gap: .75rem; margin-top: 1rem; font-size: .875rem; }
.competition-manager ::v-deep .v-btn { letter-spacing: normal; text-transform: none; font-size: .875rem; }
.competition-manager ::v-deep .v-label, .competition-manager ::v-deep input, .competition-manager ::v-deep textarea { font-size: 1rem; }
.competition-manager ::v-deep .v-messages { font-size: .75rem; }
.competition-manager ::v-deep input[type="date"] { color-scheme: dark; }
.competition-manager ::v-deep .v-text-field--outlined fieldset,
.competition-dialog ::v-deep .v-text-field--outlined fieldset { border-color: #738078; }
.competition-manager ::v-deep .v-input:not(.v-input--is-focused) .v-label,
.competition-dialog ::v-deep .v-input:not(.v-input--is-focused) .v-label { color: #c6cec9; }
.competition-manager ::v-deep .v-btn.v-btn--disabled { color: #a8b2ab !important; }
.competition-manager ::v-deep .v-tab--disabled { color: #a8b2ab !important; opacity: 1; }
.competition-dialog ::v-deep .v-card__text { color: #d6ddd8; font-size: 1rem; line-height: 1.6; }
.competition-dialog ::v-deep .v-label,
.competition-dialog ::v-deep input { font-size: 1rem; }
.competition-dialog ::v-deep .v-messages { font-size: .75rem; }
.competition-manager ::v-deep td { font-size: .875rem !important; }
.competition-manager ::v-deep th { font-size: .75rem !important; }
@media (max-width: 900px) { .editor-grid { grid-template-columns: 1fr; } }
@media (max-width: 600px) { .competition-manager { padding: .75rem; } .editor-card { padding: 1rem; } .competition-grid { grid-template-columns: minmax(0, 1fr); } .competition-card { padding: 1.25rem; } .card-primary-actions { grid-template-columns: minmax(0, 1fr); } .pagination { justify-content: center; } }
</style>
